import * as React from 'react';
import * as styled from './styled';
import * as _ from 'lodash';
import {Thumbnail} from './styled';
import {useFormikContext} from 'formik';
import {RecipeObject} from 'api/recipe/types';
import {getRecipe} from 'api/recipe/requests';
import {getImageByPreview} from 'api/library';
import {ProgramItem, ProgramObject, FoodItem} from 'api/program/types';
import ControlButton from 'components/ControlButton';
import useLoading from 'hooks/use-loading';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {RecipeProps} from './types';
import {IconTrash} from 'icons';

/**
 * The exercise card.
 * @param {RecipeProps} props
 * @return {React.ReactElement<RecipeProps>}
 * @constructor
 */
function Recipe(
    props: RecipeProps,
): React.ReactElement<RecipeProps> {
  const {
    data, fetch, render,
  } = useLoading<RecipeObject>();

  React.useEffect(() => {
    fetch(() => new Promise((resolve) => (
      getRecipe(props.id).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          resolve(response);
        }
      })
    )))
  }, [props.id]);

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
     /**
     * Render thumbnail.
     * @return {JSX.Element}
     */
    function renderThumbnail(): JSX.Element {
      return (
        <Thumbnail
          src={getImageByPreview(data?.preview)}
          alt="Изображение упражнения"
        />
      );
    }

    /**
     * Render title.
     * @return {JSX.Element}
     */
    function renderTitle(): JSX.Element {
      return (
        <styled.Title href={`/recipes/${data?.id}/view`} target="_blank">
          {data?.title}
        </styled.Title>
      );
    }

    /**
     * Render energy.
     * @return {JSX.Element}
     */
    function renderEnergy(): JSX.Element {
      const kcal = data?.ingredients.map((
        (ingredient) => (
          ingredient.energy.kcal
        )
      )).reduce((
        (prev, current) => (
          Number(prev) + Number(current)
        )
      ), 0);

      const proteins = data?.ingredients.map((
        (ingredient) => (
          ingredient.energy.proteins
        )
      )).reduce((
        (prev, current) => (
          Number(prev) + Number(current)
        )
      ), 0);

      const fats = data?.ingredients.map((
        (ingredient) => (
          ingredient.energy.fats
        )
      )).reduce((
        (prev, current) => (
          Number(prev) + Number(current)
        )
      ), 0);

      const ch = data?.ingredients.map((
        (ingredient) => (
          ingredient.energy.ch
        )
      )).reduce((
        (prev, current) => (
          Number(prev) + Number(current)
        )
      ), 0);

      return (
        <styled.Energy>
          <styled.EnergyItem>
            Калории: <span>{kcal}</span>
          </styled.EnergyItem>
          <styled.EnergyItem>
            Белки: <span>{proteins}</span>
          </styled.EnergyItem>
          <styled.EnergyItem>
            Жиры: <span>{fats}</span>
          </styled.EnergyItem>
          <styled.EnergyItem>
            Углеводы: <span>{ch}</span>
          </styled.EnergyItem>
        </styled.Energy>
      );
    }

    return (
      <styled.Wrapper>
        {renderThumbnail()}
        {renderTitle()}
        {renderEnergy()}
      </styled.Wrapper>
    );
  }

  /**
   * Render not found.
   * @return {JSX.Element}
   */
  function renderNotFound(): JSX.Element {
    return (
      <React.Fragment />
    );
  }

  return render(renderContent, renderNotFound);
}

export default React.memo(Recipe);
